import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';

const TYPE_OPTIONS = [
    { value: 'Bug', labelKey: 'tickets.typeBug', labelDefault: 'Bug' },
    { value: 'Feature', labelKey: 'tickets.typeFeature', labelDefault: 'Feature' },
    { value: 'Enhancement', labelKey: 'tickets.typeEnhancement', labelDefault: 'Enhancement' },
];

function NewTicketDialog({ open, onClose, onCreated, creating }) {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [ticketType, setTicketType] = useState('Bug');
    const [estimatedHours, setEstimatedHours] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setTitle('');
        setTicketType('Bug');
        setEstimatedHours('');
        setDescription('');
        setError('');
    }, [open]);

    const handleCreate = async () => {
        setError('');

        const trimmedTitle = title.trim();
        const trimmedDescription = description.trim();
        const trimmedEstimatedHours = estimatedHours.trim();
        const parsedEstimatedHours = trimmedEstimatedHours === '' ? null : Number(trimmedEstimatedHours);

        if (!trimmedTitle) {
            setError(t('tickets.titleRequired', 'Title is required'));
            return;
        }
        if (trimmedEstimatedHours !== '' && (!Number.isFinite(parsedEstimatedHours) || parsedEstimatedHours < 0)) {
            setError(t('tickets.estimatedHoursInvalid', 'Estimated hours must be 0 or greater'));
            return;
        }

        try {
            await onCreated({
                title: trimmedTitle,
                type: ticketType,
                estimatedHours: parsedEstimatedHours,
                description: trimmedDescription,
            });
        } catch (e) {
            setError(t('tickets.failedToCreate', 'Failed to create ticket'));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('tickets.newTicket', 'New Ticket')}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {error ? <Alert severity="error">{error}</Alert> : null}

                    <TextField
                        label={t('tickets.title', 'Title')}
                        fullWidth
                        size="small"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={creating}
                    />

                    <TextField
                        label={t('tickets.type', 'Type')}
                        select
                        fullWidth
                        size="small"
                        value={ticketType}
                        onChange={(e) => setTicketType(e.target.value)}
                        disabled={creating}
                    >
                        {TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {t(opt.labelKey, opt.labelDefault)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label={t('tickets.estimatedHours', 'Estimated Hours')}
                        fullWidth
                        size="small"
                        type="number"
                        value={estimatedHours}
                        onChange={(e) => setEstimatedHours(e.target.value)}
                        disabled={creating}
                        inputProps={{ min: 0, step: 0.25 }}
                    />

                    <TextField
                        label={t('tickets.description', 'Description')}
                        fullWidth
                        size="small"
                        multiline
                        minRows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={creating}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={creating}>{t('tickets.cancel', 'Cancel')}</Button>
                <Button variant="contained" onClick={handleCreate} disabled={creating}>{t('tickets.create', 'Create')}</Button>
            </DialogActions>
        </Dialog>
    );
}

export default NewTicketDialog;
